import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import {
  ArrowLeft, Plus, Eye, Archive, RefreshCw, FileText, Info, Calendar, Moon,
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/lib/auth';
import { getChart, DYNAMIC_TEMPLATE_CODES } from './api';
import { listEntries, archiveEntry, acknowledgeNdisEntry } from './entriesApi';
import { getTemplate } from './templatesApi';
import { BowelChartEntryList } from './BowelChartEntryList';
import { BowelChartEntryFormModal } from './BowelChartEntryFormModal';
import { BowelChartComplianceSummary } from './BowelChartComplianceBadge';
import { BowelTrendSummaryCard } from './BowelTrendSummaryCard';
import { ChartLinkingCard } from './ChartLinkingCard';
import { ChartReviewCard } from './ChartReviewCard';
import { SleepChartEntriesTable } from './SleepChartEntriesTable';
import { SleepChartEntryFormModal } from './SleepChartEntryFormModal';
import { ChartDynamicEntryForm } from './ChartDynamicEntryForm';
import { DynamicChartEntriesTable } from './DynamicChartEntriesTable';
import { FluidTrendCard } from './FluidTrendCard';
import { BehaviourTrendCard } from './BehaviourTrendCard';
import { useAppDialog } from '@/hooks/useAppDialog';

const BOWEL = 'bowel_chart';
const SLEEP = 'sleep_chart';

const TEMPLATE_FLAG_REQUIREMENTS = {
  bowel_chart: ['bowel_chart_enabled'],
  sleep_chart: ['sleep_chart_enabled', 'multi_chart_templates_enabled'],
  pain_chart: ['pain_chart_enabled', 'multi_chart_templates_enabled'],
  fluid_intake_chart: ['fluid_intake_chart_enabled', 'multi_chart_templates_enabled'],
  behaviour_chart: ['behaviour_chart_enabled', 'multi_chart_templates_enabled'],
  wound_chart: ['wound_chart_enabled', 'multi_chart_templates_enabled'],
  // Slice C — low-risk catalog templates
  personal_hygiene_chart: ['personal_hygiene_chart_enabled', 'multi_chart_templates_enabled'],
  vital_signs_chart: ['vital_signs_chart_enabled', 'multi_chart_templates_enabled'],
  blood_glucose_chart: ['blood_glucose_chart_enabled', 'multi_chart_templates_enabled'],
  exercise_therapy_chart: ['exercise_therapy_chart_enabled', 'multi_chart_templates_enabled'],
  community_access_chart: ['community_access_chart_enabled', 'multi_chart_templates_enabled'],
  weight_chart: ['weight_chart_enabled', 'multi_chart_templates_enabled'],
  // Slice D Wave 1 — non-photo high-risk templates
  neuro_obs_chart: ['neuro_obs_chart_enabled', 'multi_chart_templates_enabled'],
  mood_mental_health_chart: ['mood_mental_health_chart_enabled', 'multi_chart_templates_enabled'],
  seizure_chart: ['seizure_chart_enabled', 'multi_chart_templates_enabled'],
  post_seizure_chart: ['post_seizure_chart_enabled', 'multi_chart_templates_enabled'],
  restrictive_practice_chart: ['restrictive_practice_chart_enabled', 'multi_chart_templates_enabled'],
  // Slice D Wave 2 — photo-dependent high-risk (flags OFF until upload path verified)
  post_fall_chart: ['post_fall_chart_enabled', 'multi_chart_templates_enabled'],
  pressure_injury_chart: ['pressure_injury_chart_enabled', 'multi_chart_templates_enabled'],
};

export default function ParticipantChartDetailPage() {
  const { confirm } = useAppDialog();
  const { participantId, chartId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const perms = user?.permissions || [];
  const canView = perms.includes('participant_charts.view');
  const canCreate = perms.includes('participant_charts.create');
  const canEdit = perms.includes('participant_charts.edit');
  const canArchive = perms.includes('participant_charts.archive');
  const canAcknowledgeNdis = perms.includes('participant_charts.ndis_reportable_acknowledge');

  // null = still loading
  const [flags, setFlags] = useState(null);
  const [chart, setChart] = useState(null);
  const [participant, setParticipant] = useState(null);
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadErr, setLoadErr] = useState(null);
  const [showArchived, setShowArchived] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [templateDoc, setTemplateDoc] = useState(null);
  const [ackEntry, setAckEntry] = useState(null);
  const [ackNote, setAckNote] = useState('');
  const [ackSubmitting, setAckSubmitting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api.get('/production/feature-flags/mine')
      .then(r => { if (!cancelled) setFlags(r?.data?.data || {}); })
      .catch(() => { if (!cancelled) setFlags({}); });
    return () => { cancelled = true; };
  }, []);

  const fetchAll = useCallback(async () => {
    if (!canView) return;
    try {
      setLoading(true);
      setLoadErr(null);
      const [pRes, c] = await Promise.all([
        api.get(`/participants/${participantId}`).catch(() => null),
        getChart(participantId, chartId),
      ]);
      setParticipant(pRes?.data?.data || null);
      setChart(c);
      const rows = await listEntries(participantId, chartId, showArchived ? {} : { status: 'active' });
      setEntries(rows);
    } catch (e) {
      const msg = e?.response?.data?.error?.message || 'Failed to load chart';
      setLoadErr(msg);
      setEntries([]);
    } finally {
      setLoading(false);
    }
  }, [participantId, chartId, canView, showArchived]);

  useEffect(() => {
    if (flags === null) return;
    if (!flags.charts_module_enabled) { setLoading(false); return; }
    fetchAll();
  }, [flags, fetchAll]);

  // Template-specific enablement
  const templateCode = chart?.template_code || null;
  const isDynamic = templateCode && DYNAMIC_TEMPLATE_CODES.has(templateCode);

  useEffect(() => {
    if (!isDynamic || !templateCode) { setTemplateDoc(null); return; }
    let cancelled = false;
    getTemplate(templateCode)
      .then(t => { if (!cancelled) setTemplateDoc(t); })
      .catch(() => { if (!cancelled) setTemplateDoc(null); });
    return () => { cancelled = true; };
  }, [isDynamic, templateCode]);

  const templateEnabled = useMemo(() => {
    if (!flags) return null;
    if (!flags.charts_module_enabled) return false;
    if (!templateCode) return true; // before chart loads, don't block header
    const reqs = TEMPLATE_FLAG_REQUIREMENTS[templateCode];
    if (!reqs) return false;
    return reqs.every(k => !!flags[k]);
  }, [flags, templateCode]);

  const reviewEnabled = !!flags?.clinical_chart_review_enabled && !!flags?.charts_module_enabled;

  // ── Guards ──
  if (!canView) {
    return (
      <div className="p-6 max-w-3xl mx-auto">
        <Card><CardContent className="py-10 text-center text-sm text-muted-foreground" data-testid="detail-no-access">
          You don't have permission to view this chart.
        </CardContent></Card>
      </div>
    );
  }
  if (flags && flags.charts_module_enabled === false) {
    return (
      <div className="p-6 max-w-3xl mx-auto space-y-3" data-testid="detail-flag-off">
        <Button variant="outline" size="sm" onClick={() => navigate(`/participants/${participantId}`)}>
          <ArrowLeft className="w-4 h-4 mr-1" />Back to participant
        </Button>
        <Card>
          <CardContent className="py-10 text-center space-y-2">
            <FileText className="w-10 h-10 mx-auto text-muted-foreground" />
            <p className="text-sm text-muted-foreground">Charts are not enabled for your organisation yet.</p>
          </CardContent>
        </Card>
      </div>
    );
  }
  if (chart && templateEnabled === false) {
    const hint = templateCode === SLEEP
      ? 'Ask an administrator to enable the "Sleep Chart" and "Multi-Chart Templates" feature flags.'
      : 'Ask an administrator to enable the "Bowel Chart" feature flag.';
    return (
      <div className="p-6 max-w-3xl mx-auto space-y-3" data-testid="detail-template-off">
        <Button variant="outline" size="sm" onClick={() => navigate(`/participants/${participantId}`)}>
          <ArrowLeft className="w-4 h-4 mr-1" />Back to participant
        </Button>
        <Card>
          <CardContent className="py-10 text-center space-y-2">
            <FileText className="w-10 h-10 mx-auto text-muted-foreground" />
            <p className="text-sm text-muted-foreground">This chart template is not enabled for your organisation yet.</p>
            <p className="text-xs text-muted-foreground">{hint}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // ── Derived view state ──
  const activeCount = entries.filter(e => e.status === 'active').length;
  const archivedCount = entries.filter(e => e.status === 'archived').length;
  const participantName = participant
    ? [participant.first_name, participant.last_name].filter(Boolean).join(' ') || participant.name
    : '';
  const isSleep = templateCode === SLEEP;
  const isBowel = templateCode === BOWEL;

  const handleArchiveEntry = async (entry) => {
    if (!canArchive) return;
    const ok = await confirm({
      title: 'Archive this entry?',
      description: 'You can still view it later by toggling "Show archived" on the chart.',
      confirmLabel: 'Archive',
      variant: 'warning',
      testId: 'chart-archive-entry-confirm',
    });
    if (!ok) return;
    try {
      await archiveEntry(participantId, chartId, entry.id);
      toast.success('Entry archived');
      fetchAll();
    } catch (e) {
      toast.error(e?.response?.data?.error?.message || 'Failed to archive entry');
    }
  };

  const confirmAcknowledgeNdis = async () => {
    if (!ackEntry) return;
    try {
      setAckSubmitting(true);
      await acknowledgeNdisEntry(participantId, chartId, ackEntry.id, ackNote.trim() || undefined);
      toast.success('NDIS-reportable review acknowledged');
      setAckEntry(null);
      setAckNote('');
      fetchAll();
    } catch (e) {
      toast.error(e?.response?.data?.error?.message || 'Failed to acknowledge');
    } finally {
      setAckSubmitting(false);
    }
  };

  const defaultTitle = isSleep ? 'Sleep Chart' : (isBowel ? 'Bowel Chart' : (templateDoc?.name || 'Chart'));
  const TitleIcon = isSleep ? Moon : FileText;
  const titleIconCls = isSleep
    ? 'w-5 h-5 text-indigo-600'
    : (isBowel ? 'w-5 h-5 text-rose-600' : 'w-5 h-5 text-slate-600');
  const recordCta = isSleep ? 'Record sleep' : 'Record entry';
  const firstRecordCta = isSleep ? 'Record first sleep entry' : 'Record first entry';
  const emptyHeadline = isSleep
    ? 'No sleep entries have been recorded yet'
    : (isBowel
        ? 'No bowel chart entries have been recorded yet'
        : 'No entries have been recorded yet');
  const emptyBody = isSleep
    ? 'Record the first sleep session to start building an auditable history for this participant.'
    : (isBowel
        ? "Record the first entry to start building an auditable history for this participant's bowel chart."
        : "Record the first entry to start building an auditable history for this chart.");

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-4" data-testid="chart-detail-page">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <Button variant="outline" size="sm" onClick={() => navigate(`/participants/${participantId}`)} data-testid="back-to-participant">
          <ArrowLeft className="w-4 h-4 mr-1" />Back to participant
        </Button>
      </div>

      {/* Chart header card (shared) */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-start justify-between flex-wrap gap-3">
            <div className="min-w-0">
              <CardTitle className="text-lg flex items-center gap-2">
                <TitleIcon className={titleIconCls} />
                {loading && !chart ? <span className="animate-pulse text-muted-foreground">Loading chart…</span> : chart?.name || defaultTitle}
                {chart?.status === 'archived' && (
                  <Badge variant="outline" className="text-[10px] bg-slate-100 border-slate-300 text-slate-600">Archived chart</Badge>
                )}
              </CardTitle>
              <p className="text-xs text-muted-foreground mt-0.5">
                {participantName ? <>Participant: <span className="font-medium">{participantName}</span></> : ''}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Button size="sm" variant="outline" onClick={fetchAll} className="h-8 text-xs" data-testid="entries-refresh">
                <RefreshCw className="w-3.5 h-3.5 mr-1" />Refresh
              </Button>
              <Button size="sm" variant="outline" onClick={() => setShowArchived(v => !v)} className="h-8 text-xs" data-testid="entries-toggle-archived">
                {showArchived ? <Eye className="w-3.5 h-3.5 mr-1" /> : <Archive className="w-3.5 h-3.5 mr-1" />}
                {showArchived ? 'Hide archived' : 'Show archived'}
              </Button>
              {canCreate && chart?.status !== 'archived' && (
                <Button size="sm" onClick={() => { setEditingEntry(null); setFormOpen(true); }} className="h-8 text-xs" data-testid="entries-record-btn">
                  <Plus className="w-3.5 h-3.5 mr-1" />{recordCta}
                </Button>
              )}
            </div>
          </div>
          {chart && (
            <div className="flex flex-wrap gap-2 mt-2 text-xs text-muted-foreground">
              <Badge variant="outline" className="text-[10px]" data-testid="detail-template-code">Template: {chart.template_code}</Badge>
              {chart.monitoring_frequency && (
                <Badge variant="outline" className="text-[10px] capitalize bg-blue-50 border-blue-200 text-blue-700">
                  <Calendar className="w-2.5 h-2.5 mr-1" />{String(chart.monitoring_frequency).replace('_', ' ')}
                </Badge>
              )}
              <Badge variant="outline" className="text-[10px] bg-emerald-50 border-emerald-300 text-emerald-700" data-testid="entries-active-count">{activeCount} active</Badge>
              {archivedCount > 0 && (
                <Badge variant="outline" className="text-[10px] bg-slate-100 border-slate-300 text-slate-600" data-testid="entries-archived-count">{archivedCount} archived</Badge>
              )}
            </div>
          )}
          {chart?.notes && (
            <p className="text-xs text-muted-foreground mt-2 flex items-start gap-1">
              <Info className="w-3 h-3 mt-0.5 shrink-0" />{chart.notes}
            </p>
          )}
          {/* Compliance only makes sense for bowel template right now */}
          {isBowel && chart?.compliance && chart.status === 'active' && (
            <div className="mt-2 pt-2 border-t" data-testid="detail-compliance">
              <BowelChartComplianceSummary compliance={chart.compliance} />
            </div>
          )}
        </CardHeader>
        <CardContent>
          {/* Day 5 blocks: linking (bowel only) + review (shared) */}
          {reviewEnabled && chart && (
            <div
              className={`mb-4 grid grid-cols-1 ${isBowel ? 'lg:grid-cols-2' : ''} gap-3`}
              data-testid="detail-day5-blocks"
            >
              {isBowel && (
                <ChartLinkingCard
                  chart={chart}
                  participantId={participantId}
                  canEdit={canEdit}
                  onChanged={fetchAll}
                />
              )}
              <ChartReviewCard
                chart={chart}
                participantId={participantId}
                canEdit={canEdit}
                onChanged={fetchAll}
              />
            </div>
          )}
          {/* Bowel-only trend summary (Day 4) */}
          {isBowel && chart?.trend_summary && chart.status === 'active' && (
            <div className="mb-4" data-testid="detail-trend-summary">
              <BowelTrendSummaryCard summary={chart.trend_summary} />
            </div>
          )}
          {/* Fluid-only trend summary (Day 11) */}
          {templateCode === 'fluid_intake_chart' && chart?.fluid_trend && chart.status === 'active' && (
            <div className="mb-4" data-testid="detail-fluid-trend">
              <FluidTrendCard trend={chart.fluid_trend} />
            </div>
          )}
          {/* Behaviour-only trend summary (Day 12) */}
          {templateCode === 'behaviour_chart' && chart?.behaviour_trend && chart.status === 'active' && (
            <div className="mb-4" data-testid="detail-behaviour-trend">
              <BehaviourTrendCard trend={chart.behaviour_trend} />
            </div>
          )}
          {loading ? (
            <div className="py-8 text-center text-sm text-muted-foreground" data-testid="entries-loading">Loading entries…</div>
          ) : loadErr ? (
            <div className="py-8 text-center text-sm text-red-600" data-testid="entries-error">{loadErr}</div>
          ) : entries.length === 0 ? (
            <div className="flex flex-col items-center text-center py-10 border-2 border-dashed rounded-md bg-muted/30" data-testid="entries-empty">
              {isSleep ? <Moon className="w-10 h-10 text-muted-foreground/60 mb-2" /> : <FileText className="w-10 h-10 text-muted-foreground/60 mb-2" />}
              <h3 className="text-base font-semibold">{emptyHeadline}</h3>
              <p className="text-sm text-muted-foreground mt-1 max-w-md">{emptyBody}</p>
              {canCreate && chart?.status !== 'archived' && (
                <Button size="sm" className="mt-4" onClick={() => { setEditingEntry(null); setFormOpen(true); }} data-testid="entries-empty-record-btn">
                  <Plus className="w-4 h-4 mr-1" />{firstRecordCta}
                </Button>
              )}
            </div>
          ) : isSleep ? (
            <SleepChartEntriesTable
              entries={entries}
              canEdit={canEdit && chart?.status !== 'archived'}
              onEdit={(e) => { setEditingEntry(e); setFormOpen(true); }}
              onArchive={handleArchiveEntry}
            />
          ) : isDynamic ? (
            <DynamicChartEntriesTable
              entries={entries}
              canEdit={canEdit && chart?.status !== 'archived'}
              onEdit={(e) => { setEditingEntry(e); setFormOpen(true); }}
              onArchive={handleArchiveEntry}
              canAcknowledgeNdis={canAcknowledgeNdis}
              onAcknowledgeNdis={(e) => { setAckEntry(e); setAckNote(''); }}
            />
          ) : (
            <BowelChartEntryList
              entries={entries}
              participantId={participantId}
              chartId={chartId}
              canEdit={canEdit && chart?.status !== 'archived'}
              canArchive={canArchive}
              showArchived={showArchived}
              onEdit={(e) => { setEditingEntry(e); setFormOpen(true); }}
              onChanged={fetchAll}
            />
          )}
        </CardContent>
      </Card>

      {/* Entry form modals — template-specific */}
      {isBowel && (
        <BowelChartEntryFormModal
          open={formOpen}
          onOpenChange={setFormOpen}
          participantId={participantId}
          chartId={chartId}
          initialEntry={editingEntry}
          onSaved={fetchAll}
        />
      )}
      {isSleep && (
        <SleepChartEntryFormModal
          open={formOpen}
          onOpenChange={setFormOpen}
          participantId={participantId}
          chartId={chartId}
          entry={editingEntry}
          onSaved={fetchAll}
        />
      )}
      {isDynamic && (
        <ChartDynamicEntryForm
          open={formOpen}
          onOpenChange={setFormOpen}
          participantId={participantId}
          chartId={chartId}
          template={templateDoc}
          entry={editingEntry}
          onSaved={fetchAll}
        />
      )}

      {/* NDIS-reportable acknowledge dialog (Slice D) */}
      <Dialog open={!!ackEntry} onOpenChange={(o) => { if (!o) { setAckEntry(null); setAckNote(''); } }}>
        <DialogContent data-testid="ndis-acknowledge-dialog">
          <DialogHeader>
            <DialogTitle>Acknowledge NDIS-reportable review</DialogTitle>
            <DialogDescription>
              Confirm this NDIS-reportable entry has been reviewed and flagged to your NDIS Quality &amp; Safeguards liaison.
              This records an auditable acknowledgement. No external NDIS submission is performed.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <label className="text-xs font-medium text-muted-foreground">Acknowledgement note (optional)</label>
            <Textarea
              value={ackNote}
              maxLength={500}
              onChange={(e) => setAckNote(e.target.value)}
              placeholder="e.g. Reported to QS liaison, incident report filed."
              data-testid="ndis-acknowledge-note"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setAckEntry(null); setAckNote(''); }} data-testid="ndis-acknowledge-cancel">Cancel</Button>
            <Button onClick={confirmAcknowledgeNdis} disabled={ackSubmitting} data-testid="ndis-acknowledge-confirm">
              {ackSubmitting ? 'Saving…' : 'Acknowledge'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
